import * as THREE from 'three';
import { CelestialBody } from '@/universe/CelestialBody';
import { GLSL_ATMOSPHERE, GLSL_CONSTANTS, GLSL_DITHER, glslFloat } from './common.glsl';
import {
  GLSL_SCATTERING_UNIFORMS,
  MIE_SCALE_HEIGHT_FRACTION,
  MIN_SCALE_HEIGHT,
  ScatteringUniforms,
  createScatteringUniforms,
} from './atmosphereDefaults';

// uSteps / uLightSteps are clamped to these bounds on the TypeScript side AND
// used as the fragment shader's `for` loop bounds (GLSL ES requires a
// compile-time constant there, so the loop cannot simply run `uSteps`
// times — see the `for (int i = 0; i < N; i++) { if (i >= steps) break; }`
// pattern below). Keeping a single JS constant and interpolating it into the
// shader source is what keeps the two bounds from silently drifting apart.
const MIN_STEPS = 2;
const MAX_STEPS = 32;
const MIN_LIGHT_STEPS = 1;
const MAX_LIGHT_STEPS = 8;

/**
 * Single-scattering atmosphere.
 *
 * Rendered as the back faces of a sphere at the atmosphere's outer radius, with
 * depth testing on and depth writing off. The fragment shader ray-marches the
 * segment of the view ray that lies inside the atmospheric shell, accumulating
 * Rayleigh and Mie in-scattering with optical depth integrated toward the star.
 *
 * The ray is clipped against the planet itself analytically, which is what makes
 * the same shader work from the ground (looking up through the whole column),
 * from orbit (a thin bright limb against black), and at a terminator (the long
 * red path through dense low air). Terrain in front of the atmosphere is handled
 * by the terrain shader's own aerial perspective term, so the two never
 * double-count and there is no dependence on reading the depth buffer.
 */

const VERTEX = /* glsl */ `
precision highp float;
varying vec3 vRenderPosition;

void main() {
  vec4 worldPosition = modelMatrix * vec4(position, 1.0);
  vRenderPosition = worldPosition.xyz;
  gl_Position = projectionMatrix * viewMatrix * worldPosition;
}
`;

// Interpolated into the fragment shader below via glslFloat(), so a real
// driver sees a well-formed float literal (see glslFloat's own doc comment).
const SAMPLE_BIAS = 3.0;
const MIE_OPTICAL_DEPTH_GAIN = 1.1;
const MAX_PATH_SCALE_HEIGHTS = 40.0;
const MULTI_SCATTER_GAIN = 3.0;

const FRAGMENT = /* glsl */ `
precision highp float;

${GLSL_CONSTANTS}
${GLSL_ATMOSPHERE}
${GLSL_DITHER}

varying vec3 vRenderPosition;

${GLSL_SCATTERING_UNIFORMS}
// This shader's own, on top of the shared scattering block above.
uniform float uMieScaleHeight;
uniform float uExposure;
/** Cross-fade with the body's distant impostor. See GLSL_DITHER. */
uniform float uLayerFade;
uniform int uSteps;
uniform int uLightSteps;

/**
 * Sample placement along a ray, clustered toward one end.
 *
 * Density falls off exponentially with altitude, so uniformly spaced samples
 * spend most of their effort in near-vacuum while under-resolving the few
 * scale heights that carry almost all of the scattering. Warping the sample
 * positions is not an approximation, it is the right quadrature for the
 * integrand: the same step count resolves the atmosphere far better, or a
 * quarter of the steps resolves it just as well.
 *
 * 'warpFraction' clusters toward u = 0; 'segmentT' below maps the march's
 * [0,1] parameter onto the ray so that u = 0 is the segment's own *densest*
 * point rather than one of its ends. Clustering at an end is only right for a
 * ray whose altitude is monotonic along it — true standing on the ground, and
 * false for the case that matters most visually: a limb ray from orbit, whose
 * altitude falls to a minimum somewhere in the middle and rises again. That
 * ray entered and exited at the top of the shell, so an end-biased comb spent
 * almost every sample in near-vacuum and left the bright limb under-resolved.
 */
const float SAMPLE_BIAS = ${glslFloat(SAMPLE_BIAS)};

/**
 * Radiometric normalisation, and the reason the sky was nearly black.
 *
 * The scattering integral below produces true single-scattered *radiance* for
 * an incident irradiance of uSunIntensity. The terrain shader, like every
 * real-time Lambertian shader, computes albedo * NdotL * intensity and so has
 * already absorbed the 1/pi that converts irradiance to radiance. Handing the
 * two results to the same tone mapper without reconciling that leaves the sky
 * about twenty times too dark next to the ground it lights — daylight terrain
 * under a black sky, which is exactly what it looked like.
 *
 * The factor of pi puts the two on the same scale. The rest stands in for
 * multiple scattering, which is not modelled here and which contributes a large
 * share of a clear sky's brightness at short wavelengths.
 */
const float MULTI_SCATTER_GAIN = ${glslFloat(MULTI_SCATTER_GAIN)};
const float SKY_RADIANCE = PI * MULTI_SCATTER_GAIN;

float warpFraction(float u) {
  return (exp(SAMPLE_BIAS * u) - 1.0) / (exp(SAMPLE_BIAS) - 1.0);
}

/**
 * Maps the march parameter u in [0,1] onto a distance along the view ray,
 * clustering samples around 'tDense' (the segment's lowest, densest point,
 * at fractional position 'denseU') while still hitting 'near' at u = 0 and
 * 'far' at u = 1. Monotonically increasing in u, so the march stays
 * front-to-back and the transmission accumulated in 'viewDepth' is genuinely
 * "camera to this sample" rather than "some end of the segment to this
 * sample".
 */
float segmentT(float u, float near, float far, float tDense, float denseU) {
  if (u <= denseU) {
    float x = (denseU - u) / max(denseU, 1e-5);
    return tDense - (tDense - near) * warpFraction(x);
  }
  float x = (u - denseU) / max(1.0 - denseU, 1e-5);
  return tDense + (far - tDense) * warpFraction(x);
}

// Optical depth from origin along dir for length metres. The light ray always
// climbs away from the planet, so its dense end is always the start.
vec2 opticalDepth(vec3 origin, vec3 dir, float rayLength, int steps) {
  vec2 depth = vec2(0.0);
  for (int i = 0; i < ${MAX_LIGHT_STEPS}; i++) {
    if (i >= steps) break;
    float t0 = warpFraction(float(i) / float(steps)) * rayLength;
    float t1 = warpFraction(float(i + 1) / float(steps)) * rayLength;
    float dt = t1 - t0;
    vec3 p = origin + dir * (t0 + dt * 0.5);
    float h = length(p) - uPlanetRadius;
    depth.x += exp(-max(h, 0.0) / uScaleHeight) * dt * uDensityScale;
    depth.y += exp(-max(h, 0.0) / uMieScaleHeight) * dt * uDensityScale;
  }
  return depth;
}

void main() {
  // The camera sits at the render-frame origin; cameraPosition is supplied by
  // Three.js and stays correct even if that ever changes.
  vec3 rayOrigin = cameraPosition - uPlanetCentre;
  vec3 rayDir = normalize(vRenderPosition - cameraPosition);

  vec2 atmoHit = raySphere(rayOrigin, rayDir, uAtmosphereRadius);
  if (atmoHit.y < 0.0) discard;

  float near = max(atmoHit.x, 0.0);
  float far = atmoHit.y;

  // Clip against the planet: everything behind the horizon is occluded.
  // Occlusion by the planet, but only by ground that is actually *ahead*.
  //
  // The datum radius is not the ground: on any world with an ocean the sea
  // floor and a good deal of dry land sit below it, so treating "camera radius
  // below the datum" as "camera is underground" blacked out the entire sky
  // whenever the player stood anywhere low-lying. A negative near intersection
  // only means the datum sphere is behind the camera, which occludes nothing.
  vec2 planetHit = raySphere(rayOrigin, rayDir, uPlanetRadius);
  if (planetHit.x > 0.0) far = min(far, planetHit.x);

  // Bound the integrated segment, rather than trying to patch the result
  // afterward with an alpha adjustment (see git history in this file for
  // several attempts at exactly that, all eventually reverted). The datum
  // sphere above is an idealised radius, not the real terrain, so at low
  // altitude over ground that happens to sit below it, 'planetHit.x' can be
  // negative in every direction — nothing stops a ray aimed at real, nearby
  // ground from integrating all the way out to the far side of the outer
  // shell, thousands of kilometres through air at effectively sea-level
  // density the whole way. That is tau orders of magnitude past anything
  // physical, and an alpha multiplier applied after the fact cannot recover a
  // colour that already integrated wrong.
  //
  // The bound is a window around the segment's DENSEST point, not a length
  // measured forward from 'near'. Measuring from the near end is what made a
  // planet seen from orbit lose its atmosphere entirely: a limb ray enters
  // the shell at the top, where there is no air, so a cap of N scale heights
  // of *path* spent its whole allowance in vacuum and terminated before
  // reaching the dense part it was aimed at — the limb integrated to nothing
  // and the alpha derived from it to zero.
  //
  // The window's half-width is the chord that climbs MAX_PATH_SCALE_HEIGHTS
  // above the closest-approach radius, sqrt((r+b)^2 - r^2) expanded to
  // sqrt(b*(2r+b)) so it never subtracts two near-equal planetary radii. Past
  // that the air is exp(-40) of the densest sample and contributes nothing,
  // so this and an uncapped integral converge — while the pathological
  // sea-level-all-the-way case stays bounded, because along any such window
  // the altitude genuinely does climb.
  float tClosest = -dot(rayOrigin, rayDir);
  float tDense = clamp(tClosest, near, far);
  float denseRadius = length(rayOrigin + rayDir * tDense);
  float band = ${glslFloat(MAX_PATH_SCALE_HEIGHTS)} * max(uScaleHeight, uMieScaleHeight);
  float halfWindow = sqrt(max(0.0, band * (2.0 * denseRadius + band)));
  near = max(near, tDense - halfWindow);
  far = min(far, tDense + halfWindow);

  float rayLength = far - near;
  if (rayLength <= 0.0) discard;
  float denseU = clamp((tDense - near) / rayLength, 0.0, 1.0);

  vec3 L = normalize(uSunDirection);
  float cosTheta = dot(rayDir, L);
  float phaseR = rayleighPhase(cosTheta);
  float phaseM = miePhase(cosTheta, uMieG);

  int steps = uSteps;

  vec3 totalRayleigh = vec3(0.0);
  vec3 totalMie = vec3(0.0);
  vec2 viewDepth = vec2(0.0);

  for (int i = 0; i < ${MAX_STEPS}; i++) {
    if (i >= steps) break;
    // Front to back, always. 'viewDepth' is the optical depth from the camera
    // to the current sample, which is only what 'attenuation' needs if the
    // samples are visited in that order — an end-biased comb that happened to
    // start at the far end was accumulating transmission away from the eye.
    float t0 = segmentT(float(i) / float(steps), near, far, tDense, denseU);
    float t1 = segmentT(float(i + 1) / float(steps), near, far, tDense, denseU);
    float dt = t1 - t0;

    vec3 p = rayOrigin + rayDir * (t0 + dt * 0.5);
    float h = length(p) - uPlanetRadius;
    // Scattering coefficients are authored at a nominal sea-level density, so a
    // thicker atmosphere is optically deeper rather than intrinsically
    // brighter. Scaling the output instead would make Ashkar's 58 kg/m3 soup
    // fifty times more luminous than Halcyon's air.
    float densityR = exp(-max(h, 0.0) / uScaleHeight) * dt * uDensityScale;
    float densityM = exp(-max(h, 0.0) / uMieScaleHeight) * dt * uDensityScale;
    viewDepth += vec2(densityR, densityM);

    // Is this sample in the planet's shadow?
    vec2 lightPlanetHit = raySphere(p, L, uPlanetRadius);
    if (lightPlanetHit.x > 0.0) continue;

    vec2 lightAtmoHit = raySphere(p, L, uAtmosphereRadius);
    float lightLength = max(lightAtmoHit.y, 0.0);
    vec2 lightDepth = opticalDepth(p, L, lightLength, uLightSteps);

    vec3 tau = uRayleigh * (viewDepth.x + lightDepth.x) + vec3(uMie) * ${glslFloat(MIE_OPTICAL_DEPTH_GAIN)} * (viewDepth.y + lightDepth.y);
    vec3 attenuation = exp(-tau);

    totalRayleigh += densityR * attenuation;
    totalMie += densityM * attenuation;
  }

  vec3 color = (totalRayleigh * uRayleigh * phaseR + totalMie * uMie * phaseM)
             * uSunColor * uSunIntensity * SKY_RADIANCE * uExposure;

  // Alpha from the total extinction along the view ray, so the shell fades out
  // exactly where the air runs out rather than at an arbitrary shell boundary.
  vec3 extinction = exp(-(uRayleigh * viewDepth.x + vec3(uMie) * viewDepth.y));
  float alpha = clamp(1.0 - min(min(extinction.r, extinction.g), extinction.b), 0.0, 1.0);

  // Optical depth diverges as the view ray approaches true tangency to the
  // ground (path length through the shell grows as 1/sin(grazing angle)), so
  // extinction saturates to fully opaque within a very narrow angular band
  // right at the geometric horizon. Every attempt at fixing this so far (see
  // git history: a hard bright ring, then several shapes of alpha fade, each
  // fixing one symptom and introducing another) tried to compute a
  // corrected fade FOR the true horizon — but 'uPlanetRadius' is a fixed
  // idealised sphere, not the curved real terrain, so at close range this
  // shader has no correct place to put "the horizon" at all; every fade
  // shape was papering over that with a different-shaped patch.
  //
  // Simpler and more robust: don't try to fade the ground-ward sky to
  // anything at close range. 'up' is the LOCAL vertical at the camera's own
  // position — independent of the datum sphere entirely — so
  // 'dot(rayDir, up) < 0' is "this ray points below my local horizontal",
  // true for the whole ground-ward hemisphere regardless of where the real
  // or idealised horizon actually sits. Holding the sky's own colour solid
  // there (alpha = 1, no fade to black or to transparent) is visually right
  // at ground level — the air continues, it does not vanish — and it costs
  // nothing above the horizon, where 'up' is unchanged and this branch never
  // triggers.
  // Alpha must remain a continuous result of optical extinction. A hard
  // branch at dot(rayDir, up) == 0 draws a straight band across the sky and
  // makes it rotate with the camera.

  // color is already the radiance scattered into the view ray; it is not a
  // straight-alpha surface colour.  With premultiplied source-over blending
  // this becomes the radiative-transfer equation
  //
  //     result = inScatter + background * transmission
  //
  // so a planet, star or ring behind bright air can be extinguished but can
  // never cut a darker silhouette into that air.  Multiplying color by alpha
  // here (the old normal-blending path) under-counted the sky radiance and
  // allowed precisely that dark cut-out.
  // Dithered on the same threshold as the terrain rather than dimmed. Scaling
  // this shell's radiance instead left it compositing over the impostor's own
  // pixels mid-fade, and since the impostor already looks like a lit planet the
  // two summed: the disc brightened well past either endpoint on the way
  // across. One threshold means one representation per pixel.
  if (ditheredOut(uLayerFade, gl_FragCoord.xy)) discard;
  gl_FragColor = vec4(color, alpha);

  // See TerrainMaterial. The alpha is computed above and the chunks only touch
  // rgb, so the extinction-derived blend weight is unaffected.
  #include <tonemapping_fragment>
  #include <colorspace_fragment>
}
`;

/** The shared scattering block, plus what only the ray marcher needs. */
export interface AtmosphereUniformSet extends ScatteringUniforms {
  /** Aerosol scale height — see `MIE_SCALE_HEIGHT_FRACTION`. */
  uMieScaleHeight: THREE.IUniform<number>;
  uExposure: THREE.IUniform<number>;
  /** Cross-fade with the impostor: 1 present, 0 absent. */
  uLayerFade: THREE.IUniform<number>;
  /** View-ray march steps. */
  uSteps: THREE.IUniform<number>;
  /** Light-ray march steps, per view sample. */
  uLightSteps: THREE.IUniform<number>;
}

export function createAtmosphereMaterial(
  body: CelestialBody,
  steps: number,
  lightSteps: number,
): { material: THREE.ShaderMaterial; uniforms: AtmosphereUniformSet } {
  const atm = body.atmosphere;

  const uniforms: AtmosphereUniformSet = {
    ...createScatteringUniforms(body),
    uMieScaleHeight: { value: Math.max(MIN_SCALE_HEIGHT, atm.scaleHeight * MIE_SCALE_HEIGHT_FRACTION) },
    uExposure: { value: 1.0 },
    uLayerFade: { value: 1 },
    uSteps: { value: Math.max(MIN_STEPS, Math.min(MAX_STEPS, steps)) },
    uLightSteps: { value: Math.max(MIN_LIGHT_STEPS, Math.min(MAX_LIGHT_STEPS, lightSteps)) },
  };

  const material = new THREE.ShaderMaterial({
    uniforms: uniforms as unknown as Record<string, THREE.IUniform>,
    vertexShader: VERTEX,
    fragmentShader: FRAGMENT,
    side: THREE.BackSide,
    transparent: true,
    depthWrite: false,
    depthTest: true,
    // RGB is already premultiplied in the radiometric sense: the integrated
    // in-scatter is the source contribution, while alpha is one minus the
    // view-ray transmission.  Keep those two quantities independent.
    blending: THREE.CustomBlending,
    blendEquation: THREE.AddEquation,
    blendSrc: THREE.OneFactor,
    blendDst: THREE.OneMinusSrcAlphaFactor,
  });

  return { material, uniforms };
}
